import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import AINormalInput from '../../../ui/inputs/AINormalInput';
import '../../../styles/pages/notifications/InsightItemConfig.scss';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import AITextArea from '../../../ui/inputs/AITextArea';
import { create_custom_prompt_api_call } from '../../../lib/api/custom-prompts/custom-prompts-api-call';
import { CustomPromptCreateRequest } from '../../../interfaces/CustomPrompt';
import SettingsIconSelectInput from '../../../ui/inputs/SettingsIconSelectInput';
import ToggleSwitch from '../../../ui/buttons/ToggleSwitch';
import { RootState } from '../../../store/store';

type Props = {
  togglePopupModal: (isOpen: boolean) => void;
  onCustomPromptAdded?: () => void;
};

const AddReportsCustomPrompts = ({
  togglePopupModal,
  onCustomPromptAdded,
}: Props) => {
  const { t } = useTranslation();
  const userProfile = useSelector((state: RootState) => state.user.profile);
  const isAgencyUser = userProfile?.role === 'agency';

  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [promptName, setPromptName] = useState('');
  const [promptText, setPromptText] = useState('');
  const [outputType, setOutputType] = useState('text');
  const [syncAcrossAgency, setSyncAcrossAgency] = useState(isAgencyUser);

  const outputTypeOptions = [
    { text: t('Text'), value: 'text' },
    { text: t('Graph'), value: 'graph' },
    { text: t('Table'), value: 'table' },
  ];

  useEffect(() => {
    const isValid = promptName.trim() !== '' && promptText.trim() !== '';
    setIsSaveDisabled(!isValid);
  }, [promptName, promptText]);

  const handleSave = async () => {
    if (isSaveDisabled || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const customPromptData: CustomPromptCreateRequest = {
        name: promptName.trim(),
        prompt: promptText.trim(),
        output_type: {
          presentation: outputType,
        },
        ...(isAgencyUser ? { sync_across_agency: syncAcrossAgency } : {}),
      };

      const result = await create_custom_prompt_api_call(customPromptData);

      if (result) {
        togglePopupModal(false);
        if (onCustomPromptAdded) {
          onCustomPromptAdded();
        }
      }
    } catch (error) {
      console.error('Error creating custom prompt:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className='insightItemConfig'>
      <div className='insightItemConfig__title'>
        <div className='insightItemConfig__title--main'>
          {/* <CustomSVGs svg={bellSVG} /> */}
          <h3>{t('AddCustomPrompt')}</h3>
        </div>

        <p>{t('CreateCustomAIPromptForAnalysis')}</p>
      </div>

      <div className='insightItemConfig__inputs'>
        <AINormalInput
          changeListeners={[(e) => setPromptName(e.target.value)]}
          id='promptName'
          value={promptName}
          placeholder={t('PromptName')}
          required={true}
          type='text'
          innerPlaceholder={t('EnterPromptName')}
        />
        <AITextArea
          changeListeners={[(e) => setPromptText(e.target.value)]}
          id='promptText'
          value={promptText}
          placeholder={t('PromptText')}
          required={true}
          innerPlaceholder={t('EnterYourCustomPrompt')}
        />
        <SettingsIconSelectInput
          filterOptions={outputTypeOptions}
          marginTop='0.5rem'
          value={outputType}
          title={t('OutputType')}
          onFilterChange={(val: string) => setOutputType(val)}
        />
        {isAgencyUser && (
          <div className='insightItemConfig__toggle'>
            <ToggleSwitch
              title={t('SyncAcrossAgency')}
              titleAlignment='left'
              isOn={syncAcrossAgency}
              onClick={(checked: boolean) => setSyncAcrossAgency(checked)}
            />
          </div>
        )}
      </div>

      <div className='insightItemConfig__buttons'>
        <MainSquareButton
          value={t('Cancel')}
          type='button'
          buttonType='white'
          disabled={isSubmitting}
          onClick={() => togglePopupModal(false)}
        />
        <MainSquareButton
          value={isSubmitting ? t('Adding...') : t('AddPrompt')}
          type='button'
          disabled={isSaveDisabled || isSubmitting}
          onClick={handleSave}
        />
      </div>
    </div>
  );
};

export default AddReportsCustomPrompts;
